import React, { createContext, useState, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useClientUser } from '@/hooks';
import { ROUTE_COLORS } from '@/components/DispatchPlan/constants';
import { useSubregionCallbacks, useSubregionState } from './hooks';
import { uniq, zipObject } from 'lodash';
import { QUERY_SUBREGIONS_BY_CLIENT } from './graphql/queries';
import { SUBREGION_EXPORT_COLUMNS, SUBREGION_IMPORT_ATTRIBUTES } from './constants';
import csvDownload from 'json-to-csv-export';
import { format } from 'date-fns';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { isOnwardAdmin, businessName, user_id } = useClientUser();
    const [loading, setLoading] = useState({});
    const [selected, selectObject] = useState({});
    const [hovered, hoverObject] = useState({});
    const [filter, setFilter] = useState({});
    const [notification, setNotification] = useState({});

    const { data: subregionsData, loading: querySubregionsLoading } = useQuery(QUERY_SUBREGIONS_BY_CLIENT, {
        variables: { client_id: user_id },
    });

    const subregions = useMemo(() => {
        return subregionsData?.results || [];
    }, [subregionsData]);

    const colorsBySubregionId = useMemo(() => {
        return Object.fromEntries(
            subregions.map((o, idx) => [o.subregion_id, ROUTE_COLORS[idx % ROUTE_COLORS.length]])
        );
    }, [ROUTE_COLORS, subregions]);

    const state = useSubregionState({ subregions, selected, hovered, filter });

    const callbacks = useSubregionCallbacks(
        { query: QUERY_SUBREGIONS_BY_CLIENT, queryVariables: { client_id: user_id } },
        { setLoading, selectObject, hoverObject, setNotification }
    );

    const importSubregions = useCallback(
        (headers, body) => {
            const subregionsByName = body.reduce((acc, row) => {
                const parsed = zipObject(headers, row);
                if (Object.values(parsed).every((cell) => !cell)) return acc;

                const { name, zip } = SUBREGION_IMPORT_ATTRIBUTES;

                return {
                    ...acc,
                    [parsed[name]]: {
                        zips: uniq([...(acc[parsed[name]]?.zips || []), parsed[zip]]),
                    },
                };
            }, {});

            if (Object.values(subregionsByName).some(({ zips }) => zips.some((zip) => zip.length !== 5))) {
                setNotification({
                    severity: 'error',
                    message: 'CSV may only contain 5-digit zip codes',
                });
                return;
            }

            const subregionsToCreate = Object.entries(subregionsByName).map(([name, { zips, carriers }]) => ({
                client_id: user_id,
                name,
                zips: {
                    data: zips.map((zip) => ({ zip })),
                },
            }));

            callbacks.importSubregions(subregionsToCreate);
        },
        [user_id, callbacks]
    );

    const exportSubregions = useCallback(() => {
        const data = subregions.reduce((acc, subregion, idx) => {
            const name = callbacks.genName(subregion, idx);
            return [
                ...acc,
                ...subregion.zips.map(({ zip }) => ({
                    name,
                    zip,
                })),
            ];
        }, []);

        const columns = SUBREGION_EXPORT_COLUMNS;

        csvDownload({
            headers: columns.map((col) => col.header),
            data: data.map((row) => columns.map((col) => col.value(row))),
            filename: `${
                isOnwardAdmin ? businessName.toLowerCase().trim().replace(/\s+/g, '-') : 'onward-delivery-profile'
            }-${format(new Date(), 'yyyy-MM-dd')}.csv`,
            delimiter: ',',
        });
    }, [subregions, callbacks]);

    return (
        <Context.Provider
            value={{
                state: {
                    ...state,
                    notification,
                    subregions,
                },
                loading: {
                    subregions: loading.subregions || querySubregionsLoading,
                    zips: loading.zips || querySubregionsLoading,
                    import: loading.import,
                },
                callbacks: {
                    ...callbacks,
                    setNotification,
                    selectObject,
                    hoverObject,
                    setFilter,
                    importSubregions,
                    exportSubregions,
                    createSubregion: (subregion) =>
                        callbacks.createSubregion({ ...(subregion || {}), client_id: user_id }),
                    genColor: (subregion) => colorsBySubregionId[subregion.subregion_id],
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
